/**
 * Company Info Panel (Phase D1.1-A · Feb 2026)
 * ---------------------------------------------
 * Admin settings for commercial documents (Estimates, Invoices, CN).
 * Owns:
 *   • Company name / logo / website
 *   • Offices (Murfreesboro, La Vergne) — key/name/address/phone/email
 *   • Estimate validity days
 *   • Footer lines (printed on customer PDFs)
 *   • Accepted payment methods
 *   • WhatsApp template (used to prefill wa.me links)
 *
 * Persistence: PUT /api/commercial/settings (admin-only backend guard).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

export default function CompanyInfoPanel({ onBack }) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/commercial/settings`, {
          withCredentials: true,
        });
        setData(data);
      } catch (e) {
        toast.error(t("co_load_err"));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || !data) {
    return (
      <div className="p-6 font-mono-tech text-sm text-[var(--text-secondary)]">
        {t("co_loading")}
      </div>
    );
  }

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setOffice = (idx, patch) =>
    setData((d) => ({
      ...d,
      offices: (d.offices || []).map((o, i) => (i === idx ? { ...o, ...patch } : o)),
    }));
  const addOffice = () =>
    setData((d) => ({
      ...d,
      offices: [
        ...(d.offices || []),
        { key: `office_${Date.now()}`, name: "", address: "", phone: "", email: "" },
      ],
    }));
  const removeOffice = (idx) =>
    setData((d) => ({ ...d, offices: (d.offices || []).filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      const { data: saved } = await axios.put(
        `${API}/commercial/settings`,
        data,
        { withCredentials: true }
      );
      setData(saved);
      toast.success(t("co_saved_ok"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("co_save_err"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="company-info-panel" className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="font-mono-tech text-xs border border-[var(--border)] px-3 py-1.5 hover:bg-black hover:text-white transition-colors"
          data-testid="company-info-back"
        >
          {t("co_back")}
        </button>
        <div>
          <div className="label-overline">SET / 07</div>
          <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tighter">
            {t("co_title")}
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            {t("co_subtitle")}
          </p>
        </div>
      </div>

      <section className="bg-white border border-[var(--border)] p-5 space-y-4">
        <div className="label-overline text-[var(--text-secondary)]">{t("co_section_general")}</div>
        <Field label={t("co_field_name")}>
          <input
            data-testid="company-info-name"
            value={data.company_name || ""}
            onChange={(e) => setField("company_name", e.target.value)}
            className="w-full border border-[var(--border)] px-3 py-2 text-sm"
          />
        </Field>
        <Field label={t("co_field_logo_url")}>
          <input
            data-testid="company-info-logo-url"
            value={data.company_logo_url || ""}
            onChange={(e) => setField("company_logo_url", e.target.value)}
            placeholder="https://…/logo.png"
            className="w-full border border-[var(--border)] px-3 py-2 text-sm font-mono-tech"
          />
        </Field>
        <Field label={t("co_field_website")}>
          <input
            data-testid="company-info-website"
            value={data.website || ""}
            onChange={(e) => setField("website", e.target.value)}
            className="w-full border border-[var(--border)] px-3 py-2 text-sm"
          />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("co_field_email")}>
            <input
              data-testid="company-info-email"
              type="email"
              value={data.company_email || ""}
              onChange={(e) => setField("company_email", e.target.value)}
              placeholder="hola@rawenstn.com"
              className="w-full border border-[var(--border)] px-3 py-2 text-sm"
            />
          </Field>
          <Field label={t("co_field_phone")}>
            <input
              data-testid="company-info-phone"
              value={data.company_phone || ""}
              onChange={(e) => setField("company_phone", e.target.value)}
              placeholder="+1 615 555-0000"
              className="w-full border border-[var(--border)] px-3 py-2 text-sm"
            />
          </Field>
        </div>
        <Field label={t("co_field_validity")}>
          <input
            data-testid="company-info-validity-days"
            type="number"
            min={1}
            value={data.estimate_validity_days || 15}
            onChange={(e) =>
              setField("estimate_validity_days", parseInt(e.target.value, 10) || 15)
            }
            className="w-24 border border-[var(--border)] px-3 py-2 text-sm"
          />
        </Field>
      </section>

      <section className="bg-white border border-[var(--border)] p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="label-overline text-[var(--text-secondary)]">{t("co_section_offices")}</div>
          <button
            data-testid="company-info-add-office"
            onClick={addOffice}
            className="font-mono-tech text-xs border border-black px-3 py-1.5 hover:bg-black hover:text-white transition-colors"
          >
            {t("co_add_office")}
          </button>
        </div>
        <div className="space-y-3">
          {(data.offices || []).map((o, i) => (
            <div
              key={i}
              data-testid={`company-info-office-${o.key}`}
              className="border border-[var(--border)] p-4 grid grid-cols-1 sm:grid-cols-2 gap-3"
            >
              <Field label={t("co_office_key")}>
                <input
                  value={o.key || ""}
                  onChange={(e) => setOffice(i, { key: e.target.value })}
                  className="w-full border border-[var(--border)] px-3 py-2 text-sm font-mono-tech"
                />
              </Field>
              <Field label={t("co_office_name")}>
                <input
                  value={o.name || ""}
                  onChange={(e) => setOffice(i, { name: e.target.value })}
                  className="w-full border border-[var(--border)] px-3 py-2 text-sm"
                />
              </Field>
              <Field label={t("co_office_address")} className="sm:col-span-2">
                <input
                  value={o.address || ""}
                  onChange={(e) => setOffice(i, { address: e.target.value })}
                  className="w-full border border-[var(--border)] px-3 py-2 text-sm"
                />
              </Field>
              <Field label={t("co_office_phone")}>
                <input
                  value={o.phone || ""}
                  onChange={(e) => setOffice(i, { phone: e.target.value })}
                  className="w-full border border-[var(--border)] px-3 py-2 text-sm"
                />
              </Field>
              <Field label={t("co_office_email")}>
                <input
                  value={o.email || ""}
                  onChange={(e) => setOffice(i, { email: e.target.value })}
                  className="w-full border border-[var(--border)] px-3 py-2 text-sm"
                />
              </Field>
              <div className="sm:col-span-2 flex justify-end">
                <button
                  data-testid={`company-info-office-remove-${o.key}`}
                  onClick={() => removeOffice(i)}
                  className="font-mono-tech text-xs text-[var(--alert-overdue,#c00)] hover:underline"
                >
                  {t("co_office_remove")}
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white border border-[var(--border)] p-5 space-y-4">
        <div className="label-overline text-[var(--text-secondary)]">{t("co_section_footers")}</div>
        <Field label={t("co_field_prod_notice")}>
          <textarea
            data-testid="company-info-production-notice"
            value={data.estimate_production_notice || ""}
            onChange={(e) => setField("estimate_production_notice", e.target.value)}
            rows={3}
            placeholder="La producción comienza tras la aprobación del arte y el pago requerido, y normalmente toma hasta 15 días hábiles salvo acuerdo distinto."
            className="w-full border border-[var(--border)] px-3 py-2 text-sm"
          />
        </Field>
        <Field label={t("co_field_est_footer")}>
          <textarea
            data-testid="company-info-footer-lines"
            value={(data.estimate_footer_lines || []).join("\n")}
            onChange={(e) =>
              setField(
                "estimate_footer_lines",
                e.target.value.split("\n").map((l) => l)
              )
            }
            rows={4}
            className="w-full border border-[var(--border)] px-3 py-2 text-sm font-mono-tech"
          />
        </Field>
        <Field label={t("co_field_inv_footer")}>
          <textarea
            data-testid="company-info-invoice-footer-lines"
            value={(data.invoice_footer_lines || []).join("\n")}
            onChange={(e) =>
              setField(
                "invoice_footer_lines",
                e.target.value.split("\n").map((l) => l)
              )
            }
            rows={4}
            placeholder={t("co_field_inv_footer_placeholder")}
            className="w-full border border-[var(--border)] px-3 py-2 text-sm font-mono-tech"
          />
        </Field>
        <Field label={t("co_field_cn_footer")}>
          <textarea
            data-testid="company-info-cn-footer-lines"
            value={(data.credit_note_footer_lines || []).join("\n")}
            onChange={(e) =>
              setField(
                "credit_note_footer_lines",
                e.target.value.split("\n").map((l) => l)
              )
            }
            rows={3}
            placeholder={t("co_field_cn_footer_placeholder")}
            className="w-full border border-[var(--border)] px-3 py-2 text-sm font-mono-tech"
          />
        </Field>
        <Field label={t("co_field_pay_methods")}>
          <input
            data-testid="company-info-payment-methods"
            value={(data.accepted_payment_methods || []).join(", ")}
            onChange={(e) =>
              setField(
                "accepted_payment_methods",
                e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
              )
            }
            className="w-full border border-[var(--border)] px-3 py-2 text-sm"
          />
        </Field>
        <Field label={t("co_field_whatsapp_tpl")}>
          <textarea
            data-testid="company-info-whatsapp-template"
            value={data.whatsapp_template || ""}
            onChange={(e) => setField("whatsapp_template", e.target.value)}
            rows={3}
            className="w-full border border-[var(--border)] px-3 py-2 text-sm"
          />
          <p className="text-xs text-[var(--text-secondary)] mt-1">
            {t("co_wa_placeholders_hint")} <code>{"{contact}"}</code>, <code>{"{company}"}</code>,{" "}
            <code>{"{number}"}</code>, <code>{"{url}"}</code>
          </p>
        </Field>
      </section>

      <div className="flex justify-end">
        <button
          data-testid="company-info-save"
          disabled={saving}
          onClick={save}
          className="font-display font-bold text-sm bg-black text-white px-6 py-3 hover:bg-[var(--accent-magenta,#E5007D)] transition-colors disabled:opacity-40"
        >
          {saving ? t("saving_ellipsis") : t("co_save_btn")}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.2em] text-[var(--text-secondary)] mb-1">
        {label.toUpperCase()}
      </div>
      {children}
    </label>
  );
}
